/**
 * The telemetry vocabulary: every continuous series BrewBase can store.
 *
 * BrewBase owns this vocabulary, and `fermentation_attribute_definition` is
 * where it lives. This module holds what the vocabulary *is* — the naming
 * grammar, the unit dimensions, and how a BrewControl tag resolves to a row —
 * but not the rows themselves. A tag resolves by stripping its family prefix
 * and looking the leaf up in a `TelemetryRegistry` built from the table; there
 * is no correspondence table in either direction.
 *
 * The rows were duplicated here as a `TELEMETRY_ATTRIBUTES` array until the two
 * copies had to be kept in step by hand, by a migration writing the same list a
 * second time and a test asserting the halves had not drifted. The table is now
 * the only copy. What that costs is the compile-time `TelemetryAttributeName`
 * union: an attribute name is a `string` that a registry lookup either knows or
 * does not, checked at runtime by whoever holds the registry.
 *
 * ## Grammar
 *
 *     <subject>[_<qualifier>]_<role>
 *
 * Units are never part of a name -- the unit lives on the row. Roles are
 * uniform, not conditional: every measured series takes `_pv`, every commanded
 * one `_sp`, every normalised drive level `_out`, every cumulative `_total`.
 * The alternative -- a suffix only where both a command and a measurement
 * exist -- is unstable: the day an uncontrolled quantity gains a controller,
 * its bare name has to be renamed and every formula and chart referencing it
 * breaks.
 *
 * Two kinds of name carry no role suffix, and a row that has none must say so
 * by setting `roleless`, so the exception stays deliberate rather than becoming
 * the norm:
 *
 *   * derived quantities with no sensor behind them (`our`, `cer`, `rq`, `od`);
 *   * a line's `_scale` and `_pump_speed`, where the last token already names
 *     the quantity and there is only ever one of it. A scale has no setpoint
 *     and a pump speed in rpm has no measurement, so a role would add a word
 *     and no information.
 *
 * ## Dimensions
 *
 * Two quantities that cannot be converted by a constant factor are two names.
 * rpm and % are different names (`x_pump_speed` vs `x_pump_out`). Same-dimension
 * unit differences (g/h vs g/min, bar vs atm) are one name, converted at ingest.
 *
 * ## A near-homonym worth knowing
 *
 * Telemetry `methanol_feed_total` (g, from a node's own dosed total) and the
 * KPI variable `methanol_feed_total_g` (g, summed from recorded additions) are
 * different quantities in different systems. They are not interchangeable.
 */
// =============================================================================
// CATEGORIES
// =============================================================================
/**
 * A closed set matching the subject class. Enforced in code rather than as a
 * DB constraint; `fermentation_attribute_definition.category` stores the text.
 */
export const TELEMETRY_CATEGORIES = [
    /** A control loop on the vessel: temperature, pH, DO, agitation, gas. */
    "loop",
    /** A named addition line: methanol, nutrient, base, antifoam, water, medium. */
    "line",
    /** The vessel itself: weight, foam, level, jacket. */
    "vessel",
    /** Offgas analyser channels and what is derived from them. */
    "offgas",
    /** Values that come from a sample rather than an instrument on the vessel. */
    "sample",
    /** A fermentation parameter's setpoint as the node holds it: dilution rate, weights, limits. */
    "parameter",
];
export function isTelemetryCategory(value) {
    return TELEMETRY_CATEGORIES.includes(value);
}
// =============================================================================
// ROLES
// =============================================================================
export const TELEMETRY_ROLES = {
    /** Commanded value: what a controller is told. */
    sp: "Setpoint",
    /** Measured value: what a sensor reports. */
    pv: "Measured",
    /** Controller output as a normalised drive level. Always %. */
    out: "Output",
    /** Cumulative amount since inoculation. */
    total: "Total",
};
const ROLE_RE = /^(.*)_(pv|sp|out|total)$/;
/** Split a standard name into its subject and role. A bare name has no role. */
export function splitTelemetryRole(name) {
    const m = ROLE_RE.exec(name);
    if (!m)
        return { subject: name, role: null };
    return { subject: m[1], role: m[2] };
}
export const TELEMETRY_UNITS = {
    // temperature, canonical °C
    "°C": { dimension: "temperature", factor: 1 },
    celsius: { dimension: "temperature", factor: 1 },
    C: { dimension: "temperature", factor: 1 },
    K: { dimension: "temperature", factor: 1, offset: -273.15 },
    "°F": { dimension: "temperature", factor: 5 / 9, offset: -32 * (5 / 9) },
    // pH is its own dimension -- it converts to nothing.
    pH: { dimension: "ph", factor: 1 },
    // fraction, canonical %
    "%": { dimension: "fraction", factor: 1 },
    percent: { dimension: "fraction", factor: 1 },
    pct: { dimension: "fraction", factor: 1 },
    ppm: { dimension: "fraction", factor: 1e-4 },
    frac: { dimension: "fraction", factor: 100 },
    // rotation, canonical rpm
    rpm: { dimension: "rotation", factor: 1 },
    // volumetric flow, canonical L/min
    "L/min": { dimension: "volume_flow", factor: 1 },
    "L/h": { dimension: "volume_flow", factor: 1 / 60 },
    "mL/min": { dimension: "volume_flow", factor: 1e-3 },
    "mL/h": { dimension: "volume_flow", factor: 1e-3 / 60 },
    "m3/h": { dimension: "volume_flow", factor: 1000 / 60 },
    // pressure, canonical bar
    bar: { dimension: "pressure", factor: 1 },
    mbar: { dimension: "pressure", factor: 1e-3 },
    atm: { dimension: "pressure", factor: 1.01325 },
    kPa: { dimension: "pressure", factor: 0.01 },
    psi: { dimension: "pressure", factor: 0.0689476 },
    // mass flow, canonical g/min
    "g/min": { dimension: "mass_flow", factor: 1 },
    "g/h": { dimension: "mass_flow", factor: 1 / 60 },
    "kg/h": { dimension: "mass_flow", factor: 1000 / 60 },
    "kg/min": { dimension: "mass_flow", factor: 1000 },
    // Specific mass flow, canonical g/kg/h: a feed rate per kilogram of broth.
    // Bridges to mass_flow only through a broth mass, which the unit table cannot hold.
    "g/kg/h": { dimension: "specific_mass_flow", factor: 1 },
    "kg/kg/h": { dimension: "specific_mass_flow", factor: 1000 },
    "g/g/h": { dimension: "specific_mass_flow", factor: 1000 },
    // mass, canonical g
    mg: { dimension: "mass", factor: 1e-3 },
    g: { dimension: "mass", factor: 1 },
    kg: { dimension: "mass", factor: 1000 },
    // volume, canonical mL
    mL: { dimension: "volume", factor: 1 },
    L: { dimension: "volume", factor: 1000 },
    // cell concentration, canonical cells/mL
    "cells/mL": { dimension: "cell_concentration", factor: 1 },
    "\u00d710\u2074/mL": { dimension: "cell_concentration", factor: 1e4 },
    "\u00d710\u2075/mL": { dimension: "cell_concentration", factor: 1e5 },
    "\u00d710\u2076/mL": { dimension: "cell_concentration", factor: 1e6 },
    "\u00d710\u2077/mL": { dimension: "cell_concentration", factor: 1e7 },
    // conductivity, canonical µS
    µS: { dimension: "conductivity", factor: 1 },
    mS: { dimension: "conductivity", factor: 1000 },
    // specific gas rate, canonical mL/min/kg
    "mL/min/kg": { dimension: "specific_gas_rate", factor: 1 },
    // specific gas volume, canonical L/kg
    "L/kg": { dimension: "specific_volume", factor: 1 },
    // time, canonical s
    s: { dimension: "time", factor: 1 },
    seconds: { dimension: "time", factor: 1 },
    min: { dimension: "time", factor: 60 },
    h: { dimension: "time", factor: 3600 },
    // Per-time rates: a dilution rate.
    "1/h": { dimension: "frequency", factor: 1 },
    "1/min": { dimension: "frequency", factor: 60 },
    // Scaleless readings. Each is its own dimension so nothing converts into it.
    ratio: { dimension: "ratio", factor: 1 },
    OD: { dimension: "optical_density", factor: 1 },
    AU: { dimension: "optical_density", factor: 1 },
    "-": { dimension: "dimensionless", factor: 1 },
    X: { dimension: "fold", factor: 1 },
    level: { dimension: "level", factor: 1 },
};
/** The dimension a unit measures, or null for a unit we do not know. */
export function unitDimension(unit) {
    if (!unit)
        return null;
    return TELEMETRY_UNITS[unit]?.dimension ?? null;
}
/** True when two units measure the same thing and so may be converted. */
export function unitsCompatible(a, b) {
    const da = unitDimension(a);
    const db = unitDimension(b);
    return da !== null && da === db;
}
/**
 * `value` expressed in `to` instead of `from`, or null when the two are not
 * the same dimension. Identical units short-circuit, so a value that needs no
 * conversion is bit-for-bit unchanged.
 */
export function convertTelemetryUnit(value, from, to) {
    if (from === to)
        return value;
    if (!from || !to)
        return null;
    const f = TELEMETRY_UNITS[from];
    const t = TELEMETRY_UNITS[to];
    if (!f || !t || f.dimension !== t.dimension)
        return null;
    const canonical = value * f.factor + (f.offset ?? 0);
    return (canonical - (t.offset ?? 0)) / t.factor;
}
/** Dimensions whose units differ by an offset, so a gain or delta in them must not be converted. */
const AFFINE_DIMENSIONS = new Set(["temperature"]);
/** True when a tuning value in `unit` may be shown in another unit of its dimension. */
export function convertibleForTuning(unit) {
    const dimension = unitDimension(unit);
    return dimension !== null && !AFFINE_DIMENSIONS.has(dimension);
}
// =============================================================================
// Display units
// =============================================================================
/** The procedure preference key for a dimension's display unit, e.g. `signal:mass_flow`. */
export const signalUnitPrefKey = (dimension) => `signal:${dimension}`;
/**
 * The unit a dimension is shown in when no preference says otherwise. Most
 * dimensions are shown as reported; these are the ones whose canonical unit
 * (decision 0001) is not what anyone reads or types. The specific feed rate
 * is stored per gram of broth, which makes every value a few thousandths;
 * the protocol is written per kilogram.
 */
const DEFAULT_DISPLAY_UNITS = {
    specific_mass_flow: "g/kg/h",
};
/** The unit `dimension` is shown in absent a preference, or null to show it as reported. */
export function defaultDisplayUnit(dimension) {
    return DEFAULT_DISPLAY_UNITS[dimension] ?? null;
}
const UNIT_SYMBOLS = {
    celsius: "°C",
    C: "°C",
    percent: "%",
    pct: "%",
};
/** The symbol of a unit a device config spells out: `percent` reads `%`. */
export function unitSymbol(unit) {
    return UNIT_SYMBOLS[unit] ?? unit;
}
/**
 * The unit a signal reported in `sourceUnit` is shown and edited in: the
 * procedure's preference for the unit's dimension, else the dimension's
 * default, else the reported unit as its symbol. A unit we do not know is shown as is.
 */
export function signalDisplayUnit(sourceUnit, preferences) {
    const dimension = unitDimension(sourceUnit);
    if (!dimension || !sourceUnit)
        return sourceUnit ?? null;
    const preferred = preferences?.[signalUnitPrefKey(dimension)] ?? DEFAULT_DISPLAY_UNITS[dimension];
    return preferred && unitsCompatible(sourceUnit, preferred)
        ? preferred
        : unitSymbol(sourceUnit);
}
/**
 * The unit a fermentation parameter authored in `sourceUnit` is shown and
 * edited in. A per-parameter feed preference wins when it measures the same
 * thing; otherwise the dimension preference, otherwise the unit itself. A
 * specific rate stays specific: it never resolves to an absolute flow.
 */
export function parameterDisplayUnit(parameterTypeCode, sourceUnit, preferences) {
    const perParameter = preferences?.[`feedRateUnit:${parameterTypeCode}`];
    if (perParameter && unitsCompatible(sourceUnit, perParameter))
        return perParameter;
    return signalDisplayUnit(sourceUnit, preferences);
}
/** Every known unit of `unit`'s dimension, the choices a preference picker offers. */
export function unitsOfDimension(dimension) {
    return Object.entries(TELEMETRY_UNITS)
        .filter(([, spec]) => spec.dimension === dimension)
        .map(([unit]) => unit);
}
export function telemetryRegistry(rows) {
    const attributes = rows.map((r) => ({
        name: r.name,
        displayName: r.display_name,
        category: r.category !== null && isTelemetryCategory(r.category)
            ? r.category
            : null,
        unit: r.unit,
        roleless: r.roleless ?? false,
        quantity: r.quantity ?? null,
        decimals: r.decimal_places ?? null,
    }));
    const byName = new Map(attributes.map((a) => [a.name, a]));
    return {
        attributes,
        has: (name) => byName.has(name),
        get: (name) => byName.get(name),
        byCategory: () => TELEMETRY_CATEGORIES.map((category) => ({
            category,
            attributes: attributes.filter((a) => a.category === category),
        })).filter((g) => g.attributes.length > 0),
    };
}
/** A registry with nothing in it. For tests, and for a not-yet-loaded client. */
export const EMPTY_TELEMETRY_REGISTRY = telemetryRegistry([]);
/**
 * What is wrong with a name, or null when nothing is.
 *
 * The grammar is checked here rather than as a DB constraint so the message can
 * say what to do about it, and so the same call validates a name typed into an
 * admin form and a name already in the table.
 */
export function telemetryNameProblem(name, roleless) {
    if (!/^[a-z][a-z0-9_]*$/.test(name)) {
        return "a name is lower-case letters, digits and underscores, starting with a letter";
    }
    const { role } = splitTelemetryRole(name);
    if (role === null && !roleless) {
        return `'${name}' has no role suffix; add _pv, _sp, _out or _total, or mark it roleless if the last token already names the quantity`;
    }
    if (role !== null && roleless) {
        return `'${name}' ends in _${role}, so it is not roleless`;
    }
    return null;
}
export const TELEMETRY_CATEGORY_LABELS = {
    loop: "Control loops",
    line: "Addition lines",
    vessel: "Vessel",
    offgas: "Offgas",
    sample: "Sample",
    parameter: "Parameters",
};
// =============================================================================
// TAG RESOLUTION
// =============================================================================
/**
 * The family a BrewControl tag belongs to. Purely informational: resolution
 * ignores it, because the leaf alone names the quantity.
 */
export const TAG_FAMILIES = [
    "inputs",
    "outputs",
    "setpoints",
    "state",
];
/**
 * The leaf of a BrewControl tag: the text after the last `.`.
 *
 * A tag is one of `inputs.<leaf>`, `outputs.<leaf>`, `setpoints.<leaf>` or
 * `<controller>.state.<leaf>`. The family is stripped because it describes how
 * the node produced the number, not what the number is -- a feed rate measured
 * by a controller and one read from a device are the same quantity.
 */
export function telemetryLeaf(tag) {
    if (/^[^.]+\.mode$/.test(tag))
        return tag.replace(/\.mode$/, "_mode");
    const at = tag.lastIndexOf(".");
    return at === -1 ? tag : tag.slice(at + 1);
}
/**
 * Resolve a node's tags against the registry.
 *
 * A tag whose leaf is a registry name is a registry series; every other tag is
 * a local series, identified by its full tag on the run and stored in the unit
 * the node reported. Local is the ordinary case for controller state and for
 * any alias outside the vocabulary; it is not reported.
 *
 * A pure function of the tag list, so the same call validates a wiring on the
 * BrewBase side before a run starts and projects a run's tags at read time.
 */
export function resolveTelemetryTags(registry, tags) {
    const byName = new Map();
    for (const tag of tags) {
        const leaf = telemetryLeaf(tag);
        if (!registry.has(leaf))
            continue;
        const bucket = byName.get(leaf);
        if (bucket) {
            if (!bucket.includes(tag))
                bucket.push(tag);
        }
        else {
            byName.set(leaf, [tag]);
        }
    }
    const resolved = new Map();
    const superseded = [];
    const duplicates = [];
    for (const [name, tagList] of byName) {
        const chosen = tagList.length === 1 ? tagList[0] : collapseIoPair(name, tagList);
        if (chosen === null) {
            duplicates.push({ name, tags: tagList });
            continue;
        }
        resolved.set(chosen, name);
        for (const tag of tagList) {
            if (tag !== chosen)
                superseded.push(tag);
        }
    }
    return { resolved, superseded, duplicates };
}
/**
 * `inputs.X` and `outputs.X` are one io point, not two quantities.
 *
 * A bidirectional device signal — every BrewLink pump's `rpm` — is polled as an
 * input and driven as an output under the same wiring key, so the node exports
 * both. They carry the same number: the pump echoes what it was commanded.
 * Rejecting the pair as a duplicate would throw away the whole series, and
 * picking arbitrarily would flip between the two.
 *
 * The commanded side wins, because that is the value the node owns and the one
 * that exists even when the device stops reporting.
 *
 * This is narrow on purpose. It applies only to `inputs.X` paired with
 * `outputs.X` for the same X. Any other pair — a setpoint next to a PLC
 * write-back that was mistakenly given the setpoint's own name, two controllers
 * exporting one state name — stays a duplicate and neither resolves, because
 * those really are two different quantities. Both stay local.
 */
function collapseIoPair(name, tags) {
    if (tags.length !== 2)
        return null;
    const families = new Set(tags.map((t) => t.slice(0, t.length - name.length - 1)));
    if (families.size !== 2 ||
        !families.has("inputs") ||
        !families.has("outputs")) {
        return null;
    }
    return `outputs.${name}`;
}
// =============================================================================
// RUN TAG PROJECTION
// =============================================================================
/**
 * The factor that turns a value in `sourceUnit` into the attribute's unit, or
 * null when the two measure different things.
 *
 * No unit, or one BrewBase does not know, is trusted: that is the only option
 * for a controller state field, and not evidence of a mismatch. Offsets are
 * refused rather than applied, because a K or °F mis-declaration corrected
 * silently is worse than one refused.
 */
export function telemetryUnitScale(registry, attributeName, sourceUnit) {
    const attribute = registry.get(attributeName);
    if (!attribute?.unit || !sourceUnit || sourceUnit === attribute.unit)
        return 1;
    if (unitDimension(sourceUnit) === null)
        return 1;
    const one = convertTelemetryUnit(1, sourceUnit, attribute.unit);
    const zero = convertTelemetryUnit(0, sourceUnit, attribute.unit);
    if (one === null || zero === null || zero !== 0)
        return null;
    return one;
}
/**
 * How a run's observed tags land on the registry. A tag that is absent from the
 * map is a local series: no registry row, a duplicate claim, or a unit that
 * cannot become the row's. It is still stored and still readable by tag.
 *
 * A pure function, so the same call answers the wiring report before a run and
 * the read path after: nothing about a tag is decided at ingest.
 */
export function projectRunTags(registry, tags) {
    const resolution = resolveTelemetryTags(registry, tags.map((t) => t.tag));
    const resolved = new Map();
    for (const { tag, unit } of tags) {
        const name = resolution.resolved.get(tag);
        if (name === undefined)
            continue;
        const scale = telemetryUnitScale(registry, name, unit);
        if (scale !== null)
            resolved.set(tag, { name, scale });
    }
    return resolved;
}
//# sourceMappingURL=telemetry-attributes.js.map